import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { DefinedBenefitContributionTableStyled } from './DefinedContributionStyled';
import { PsDrawerDialog } from 'stencilSetup';
import { CategoryResponseDto } from 'enrollment/queries/useBenefitCategoryQuery/types';
import { determineColor, SpecificIcon } from 'enrollment/utils/common/Utils';
import { totalDefinedBenefitContributionSelector } from 'enrollment/services/Benefit/selectors';
import {
  selectEnrollmentContext,
  selectEnrollmentOrgPlanYearId,
} from 'theSource/features/Home/components/EnrollmentBanner/services';
import { queryKeyGenerator } from 'theSource/utils';
import { useBenefitCategoryQuery } from 'enrollment/queries';
import { useConfigurationQuery } from 'theSource/queries';

interface DefinedBenefitContributionDrawerProps {
  benefits?: CategoryResponseDto;
  btnName?: string;
  category?: string;
}

export const DefinedBenefitContributionDrawer = ({
  benefits,
  btnName,
  category,
}: DefinedBenefitContributionDrawerProps) => {
  const { t } = useTranslation('enrollment.benefits');
  const totalDefinedBenefitContribution = useSelector(
    totalDefinedBenefitContributionSelector
  );
  const enrollmentContextType = useSelector(selectEnrollmentContext);
  let orgPlanYearContextId = useSelector(selectEnrollmentOrgPlanYearId);
  orgPlanYearContextId =
    enrollmentContextType === 'life_event' ? orgPlanYearContextId : 0;

  const { configuration } = useConfigurationQuery();
  const lumosApiHost = configuration?.apiHost;

  const formatDollarAmount = (amount: string) => {
    const numericValue = parseFloat(amount);
    return numericValue;
  };

  const { data } = useBenefitCategoryQuery({
    enrollmentContextType: enrollmentContextType,
    enabled: Boolean(!benefits && enrollmentContextType && category),
    keepPreviousData: true,
    lumosApiHost: String(lumosApiHost),
    key: queryKeyGenerator(
      `${category}-${enrollmentContextType}`,
      Number(orgPlanYearContextId)
    ),
    getParamCategory: true,
    includeEligibleDependentsData: true,
    category: category ? category : '',
    orgPlanYearId: orgPlanYearContextId,
  });

  const benefitData = benefits ? benefits : data;

  if (
    benefitData &&
    benefitData.data.defined_benefit_contributions &&
    benefitData.data.defined_benefit_contributions.length > 0 &&
    !benefitData.data.defined_benefit_contributions.find(
      (dbc) => dbc.show_in_enrollment === false
    ) &&
    totalDefinedBenefitContribution &&
    totalDefinedBenefitContribution > 0
  ) {
    return (
      <PsDrawerDialog
        dialogId="Defined-benefit-contribution-drawer"
        dialogTriggerLabel={btnName || t('common:viewMore')}
        dialogTriggerClass={`${btnName ? 'btn btn-secondary btn-link show-underline bolder' : 'content-link ml-2'} text-nowrap`}
        dialogHeading={t(
          'definedBenefitContribution.definedBenefitDrawerTitle'
        )}
        dialogLarge
        dialogTriggerLink
      >
        <div>
          <p>{t('definedBenefitContribution.definedBenefitDrawerSubTitle')}</p>

          <DefinedBenefitContributionTableStyled>
            <table className="table">
              <thead>
                <tr className="border-bottom">
                  <th className="body semiBold w-50 text-left">
                    {t('definedBenefitContribution.tableBenefit')}
                  </th>
                  <th className="body semiBold text-left">
                    {t('definedBenefitContribution.tableBenefitCost')}
                  </th>
                  <th className="body semiBold text-center">
                    {t('definedBenefitContribution.tableCredit')}
                  </th>
                  <th className="body semiBold text-right">
                    {t('definedBenefitContribution.tableCost')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {benefitData?.data.benefits.map((benefit) => {
                  // PS_Spyder does not follow hide cost if empty flag on defined benefit contribution. Following the same pattern here
                  if (
                    benefit.coverage?.req_coverage_level_lookup_code !==
                    'decline'
                  ) {
                    const yourCost = benefit.coverage?.req_subscriber_premium
                      ? formatDollarAmount(
                          benefit.coverage?.req_subscriber_premium
                        )
                      : undefined;
                    const credit =
                      benefit.coverage?.defined_benefit_credits?.reduce(
                        (sum, credit) =>
                          sum + formatDollarAmount(credit.pay_period_amount),
                        0
                      ) || undefined;
                    const benefitCost =
                      typeof yourCost === 'number'
                        ? yourCost + (typeof credit === 'number' ? credit : 0)
                        : undefined;

                    return (
                      <tr key={benefit.benefit.org_benefit_id}>
                        <td className="text-left d-flex p-0 mt-2_5">
                          <span
                            className={`icon-container ${determineColor(benefit.benefit.org_benefit_category)}`}
                          >
                            <SpecificIcon
                              benefitType={benefit.benefit.benefit_lookup_code}
                            />
                          </span>
                          <span className="ml-2_5">
                            <p className="semiBold mb-0 mt-2">
                              {benefit.benefit.org_benefit_name}
                            </p>
                            <p className="helper-text mb-0">
                              {t('definedBenefitContribution.costOfPlan')}
                            </p>
                          </span>
                        </td>
                        <td className="semiBold text-left pt-4_5">
                          {typeof benefitCost === 'number'
                            ? `$${benefitCost.toFixed(2)}`
                            : 'N/A'}
                        </td>
                        <td className="semiBold text-center pt-4_5">
                          {typeof credit === 'number'
                            ? `$${credit.toFixed(2)}`
                            : 'N/A'}
                        </td>
                        <td className="semiBold text-right pt-4_5">
                          {typeof yourCost === 'number'
                            ? `$${yourCost.toFixed(2)}`
                            : typeof benefitCost === 'number'
                              ? `$${benefitCost.toFixed(2)}`
                              : 'N/A'}
                        </td>
                      </tr>
                    );
                  } else {
                    return <></>;
                  }
                })}
              </tbody>
            </table>
            {/* total employer contribution */}
            <div className="d-flex semiBold border-top pt-4 justify-content-between">
              <p className="h3">
                {t('definedBenefitContribution.employerCredit')}
                <span className="ml-2 headline-1">
                  ${totalDefinedBenefitContribution.toFixed(2)}
                </span>
              </p>
              <p className="h3">
                {t('definedBenefitContribution.costppp')}
                <span className="ml-2 headline-1">
                  $
                  {formatDollarAmount(
                    benefitData.data.cart_total || '0'
                  ).toFixed(2)}
                </span>
              </p>
            </div>
          </DefinedBenefitContributionTableStyled>
        </div>
      </PsDrawerDialog>
    );
  }
};
